'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import styles from './notFoundRedirect.module.css';

const REDIRECT_DELAY_SECONDS = 10;

function isWordPressBlogPath(pathname: string) {
  return pathname === '/blog' || pathname.startsWith('/blog/');
}

interface NotFoundRedirectProps {
  homeHref: string;
  messagePrefix: string;
  secondsLabel: string;
}

export default function NotFoundRedirect({ homeHref, messagePrefix, secondsLabel }: NotFoundRedirectProps) {
  const pathname = usePathname() ?? '';
  const isBlogPath = isWordPressBlogPath(pathname);
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS);

  useEffect(() => {
    // WordPress owns /blog and /blog/*; leave those paths available to
    // Apache/Passenger instead of navigating them to the Next home page.
    if (isBlogPath) return;

    const redirectAt = Date.now() + REDIRECT_DELAY_SECONDS * 1000;
    const interval = window.setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil((redirectAt - Date.now()) / 1000)));
    }, 250);
    const timeout = window.setTimeout(() => {
      window.location.assign(homeHref);
    }, REDIRECT_DELAY_SECONDS * 1000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [homeHref, isBlogPath]);

  if (isBlogPath) return null;

  return (
    <p className={styles.redirectMessage} role="status" aria-live="polite">
      {messagePrefix}{' '}
      <strong>{secondsLeft} {secondsLabel}</strong>.
    </p>
  );
}
